import { SearchField } from '@shared/components';
import { SearchFieldProps } from '@shared/components/search-field/SearchField';
import { QueryParamName } from 'clerk_common/constants/urls/queryParams';
import { parseAsString, useQueryStates } from 'nuqs';
import {
  ReactElement,
  startTransition,
  useEffect,
  useRef,
  useState,
} from 'react';
import { shouldAdoptSearchUrl } from './searchInputSync';

type GlobalSearchFieldProps = Pick<
  SearchFieldProps,
  'hasIcon' | 'placeholder' | 'size'
> & {
  debounceMs?: number;
};

export function GlobalSearchField({
  debounceMs = 0,
  ...p
}: GlobalSearchFieldProps): ReactElement {
  const [queryParams, setQueryParams] = useQueryStates({
    [QueryParamName.SEARCH_TERM]: parseAsString,
  });
  const urlValue = queryParams[QueryParamName.SEARCH_TERM] ?? '';
  const [value, setValue] = useState(urlValue);
  const valueRef = useRef(value);
  const emittedUrlValue = useRef<string | null>(null);
  valueRef.current = value;

  useEffect(() => {
    if (debounceMs <= 0) return;
    if (
      !shouldAdoptSearchUrl(urlValue, valueRef.current, emittedUrlValue.current)
    ) {
      return;
    }
    setValue(urlValue);
  }, [debounceMs, urlValue]);

  useEffect(() => {
    if (debounceMs <= 0 || value === urlValue) return;
    const timeout = setTimeout(() => {
      emittedUrlValue.current = value;
      startTransition(() => {
        void setQueryParams(
          { [QueryParamName.SEARCH_TERM]: value },
          { shallow: true }
        );
      });
    }, debounceMs);
    return () => clearTimeout(timeout);
  }, [debounceMs, setQueryParams, urlValue, value]);

  if (debounceMs <= 0) {
    return (
      <SearchField
        {...p}
        autoFocus
        value={urlValue}
        onChange={(next) =>
          setQueryParams(
            { [QueryParamName.SEARCH_TERM]: next },
            { shallow: true }
          )
        }
      />
    );
  }

  return <SearchField {...p} autoFocus value={value} onChange={setValue} />;
}
